// The captures the server took and REFUSED.
//
// ⚠⚠ WHY THIS SCREEN EXISTS AT ALL. Rule 4 of this app says a rejected reading is
// STORED, never dropped — `IngestVerdict = REJECTED` with a `RejectReason`,
// because "a refusal that is invisible is a slower silent loss". The server kept
// that promise from day one. The device did not.
//
// The queue's only channel for a server refusal was an `onOutcome` callback that
// defaulted to `() => {}`, and no production code ever supplied one. So a reading
// refused for a lapsed competency, an overdue calibration or an unreleased line
// was deleted from the device, the sync chip went green, and the operator who
// took it walked away believing it was captured. The refusal existed, in a table
// nobody at the line reads.
//
// This banner is the other half of rule 4. It is deliberately NOT dismissible by
// swiping past: the button says the person read it, and until they press it the
// banner stays through navigation, because the capture they think they made is
// not there.
//
// ⚠ IT DOES NOT OFFER A RETRY, AND THAT IS THE POINT. A refusal is not a network
// failure. Re-sending the same reading against the same lapsed competency
// produces the same refusal, and a retry button would teach people to press it
// instead of fixing the thing that is wrong. The sentence from the server names
// what is wrong; the fix is a grant, a calibration, or a start-up check.
import { useState } from 'react';
import type { QueueRefusal } from '../../offline/queue';
import { opFocus } from '../../shells/OperatorShell';

interface RefusedBannerProps {
  items: QueueRefusal[];
  /** Refusals past the queue's cap. Shown so the count is never a quiet lie. */
  dropped: number;
  onAcknowledge: (clientCaptureIds: string[]) => void;
}

/**
 * Every reason the capture gate can refuse a reading.
 *
 * ⚠⚠ MIRRORS `RejectReason` IN api/src/domain/gateRules.ts, AND THE FIRST VERSION
 * OF THIS FILE GOT IT WRONG. It invented `no_current_grant` and
 * `meter_out_of_calibration` — neither exists; the real codes are
 * `not_authorised` and `meter_calibration_overdue` — and it listed `verify_self`,
 * which belongs to the VERIFY gate and can never reach a capture. The map was
 * typed `Record<string, string>`, so nothing complained, and the effect was
 * invisible in the worst way: the lookup simply missed and the operator saw the
 * server's sentence with no heading, which looks exactly like a code we chose not
 * to translate.
 *
 * Declaring the union and typing the map against it makes a typo a compile error.
 * `api/src/__tests__/vocabulary.test.ts` checks this list against the API's union,
 * for the same reason it checks the activity lists against their CHECK
 * constraints: two declarations of one vocabulary drift, and this one drifted
 * within a day of being written.
 */
type RejectReason =
  | 'run_not_found'
  | 'run_closed'
  | 'pilot_ended'
  | 'readiness_outstanding'
  | 'attribute_not_boolean'
  | 'parameter_not_found'
  | 'parameter_not_in_run_spec'
  | 'parameter_inactive'
  | 'clock_implausible'
  | 'not_authorised'
  | 'competency_missing'
  | 'competency_expired'
  | 'meter_required'
  | 'meter_not_found'
  | 'meter_kind_mismatch'
  | 'meter_out_of_service'
  | 'meter_calibration_overdue'
  | 'evidence_required'
  | 'no_values'
  | 'sample_count_mismatch'
  | 'too_many_samples'
  | 'aqs_no_nominal'
  | 'aqs_no_tolerances'
  | 'unknown_aggregation';

/**
 * The ones worth a heading, in the operator's words. The server's own sentence is
 * always shown underneath and is the authoritative one; this is what somebody
 * reads first, standing at a line, deciding who to go and find.
 *
 * Partial on purpose. A refusal like `unknown_aggregation` is a spec that was
 * written wrong, not something the person holding the iPad can act on, and a
 * plain-English gloss would only imply they could.
 */
const PLAIN: Partial<Record<RejectReason, string>> = {
  readiness_outstanding: 'The line has not been released yet.',
  not_authorised: 'You are not authorised to capture this check.',
  competency_missing: 'You have no recorded competency for this check.',
  competency_expired: 'Your competency for this check has expired.',
  meter_calibration_overdue: 'That instrument is out of calibration.',
  meter_out_of_service: 'That instrument is out of service.',
  meter_required: 'This check has to be taken with a registered instrument.',
  evidence_required: 'This check needs evidence before it can be recorded.',
  run_closed: 'That run was closed before this reading reached the server.',
  pilot_ended: 'That was a pilot run, and pilot mode has ended. Close it and start a new run.',
  clock_implausible: "The device clock and the server disagree by more than a day.",
  attribute_not_boolean: 'That answer was neither yes nor no.',
  sample_count_mismatch: 'The spec expects a different number of samples.',
};

/**
 * The heading for a stored reason, or nothing.
 *
 * The cast lives HERE and nowhere else. `QueueRefusal.reason` is a plain string
 * because it came off the wire — the server could ship a code this build has
 * never heard of, and a union type would be a promise the network cannot keep.
 * Narrowing once, in one function, is what lets the map itself stay typed so a
 * misspelt key is a compile error. An unknown code simply has no heading, and the
 * server's own sentence carries the message.
 */
function plainFor(reason: string | null): string | undefined {
  return reason ? PLAIN[reason as RejectReason] : undefined;
}

/** The check's name when the queue row carried one, else its code. The operator
 *  thinks in the words on the form, not in the spec's identifier. */
function refusalLabel(r: Pick<QueueRefusal, 'parameterName' | 'parameterCode'>): string {
  return r.parameterName?.trim() || r.parameterCode || 'A check';
}

function when(ms: number): string {
  const d = new Date(ms);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function RefusedBanner({ items, dropped, onAcknowledge }: RefusedBannerProps) {
  const [acknowledging, setAcknowledging] = useState(false);
  if (items.length === 0) return null;

  return (
    <div
      role="alert"
      className="m-(--s4) rounded-(--radius) border-2 border-(--out-line) bg-(--out-bg) p-(--s4)"
    >
      <h2 className="m-0 text-(length:--fs-lg) font-bold text-(--out-fg)">
        {items.length === 1
          ? 'One check was refused and NOT recorded against this run'
          : `${items.length} checks were refused and NOT recorded against this run`}
        {dropped > 0 ? `, and ${dropped} more` : ''}
      </h2>
      <p className="m-0 mt-(--s2) text-(length:--fs-md)">
        The server kept a record of each attempt, so nothing is hidden — but{' '}
        {items.length === 1 ? 'this check has not been' : 'these checks have not been'} answered.
        Fix what it names below, then take {items.length === 1 ? 'it' : 'them'} again.
      </p>

      <ul className="m-0 mt-(--s3) list-none p-0">
        {items.map((r) => (
          <li
            key={r.clientCaptureId}
            className="mt-(--s2) rounded-(--radius) bg-(--card) p-(--s3)"
          >
            <p className="m-0 text-(length:--fs-md) font-semibold">
              {refusalLabel(r)}
              {when(r.at) ? (
                <span className="ml-(--s2) font-mono text-(length:--fs-sm) font-normal text-(--muted)">
                  {when(r.at)}
                </span>
              ) : null}
            </p>
            {plainFor(r.reason) ? (
              <p className="m-0 mt-(--s1) text-(length:--fs-md) font-semibold">
                {plainFor(r.reason)}
              </p>
            ) : null}
            {/* The server's own sentence, verbatim. It is written for the person
                at the line and names what to do; replacing it with a category
                throws away the only actionable part. */}
            {r.message ? (
              <p className="m-0 mt-(--s1) text-(length:--fs-md) text-(--muted)">
                {r.message}
              </p>
            ) : null}
            {!r.message && !plainFor(r.reason) ? (
              <p className="m-0 mt-(--s1) text-(length:--fs-md) text-(--muted)">
                The server refused it without saying why
                {r.reason ? ` (${r.reason})` : ''}. Tell a supervisor before running on.
              </p>
            ) : null}
          </li>
        ))}
      </ul>

      <button
        type="button"
        disabled={acknowledging}
        onClick={() => {
          setAcknowledging(true);
          onAcknowledge(items.map((r) => r.clientCaptureId));
          setAcknowledging(false);
        }}
        className={`mt-(--s4) min-h-[56px] w-full rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-lg) font-bold ${opFocus}`}
      >
        I have read this
      </button>
    </div>
  );
}

export default RefusedBanner;
